import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Calendar, 
  MapPin, 
  Users, 
  Tag, 
  Clock, 
  User,
  Globe,
  Share2,
  Bell,
  Video,
  CheckCircle,
  Timer,
  ExternalLink,
  Facebook,
  Twitter,
  MessageCircle,
  Copy,
  Badge,
  Zap
} from 'lucide-react';
import { settingsAPI } from '../services/api';

interface Speaker {
  id: string;
  name: string;
  title?: string;
  bio?: string;
  imageUrl?: string;
}

interface Category {
  id: string;
  name: string;
  color?: string;
}

interface Location {
  id: string;
  name: string;
  address: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  capacity?: number;
  amenities?: string[];
  imageUrl?: string;
}

interface Event {
  id: string;
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  imageUrl?: string;
  streamUrl?: string;
  isStreaming: boolean;
  capacity?: number;
  currentAttendees: number;
  status: 'UPCOMING' | 'ACTIVE' | 'FINISHED' | 'CANCELLED';
  tags: string[];
  categories?: Category[];
  locations?: Location[];
  location?: string; // Backward compatibility for simple location string
  speakers?: Speaker[];
}

interface EventDetailModalProps {
  event: Event | null;
  isOpen: boolean;
  onClose: () => void;
  onRegister?: (eventId: string) => void;
  onSetReminder?: (eventId: string) => void;
  isRegistered?: boolean;
  hasReminder?: boolean;
}

interface AppSettings {
  showCapacityIndicator?: boolean;
  allowRegistration?: boolean;
  [key: string]: any;
}

export const EventDetailModal: React.FC<EventDetailModalProps> = ({
  event,
  isOpen,
  onClose,
  onRegister,
  onSetReminder,
  isRegistered = false,
  hasReminder = false
}) => {
  const [mapUrl, setMapUrl] = useState<string>('');
  const [shareUrl, setShareUrl] = useState<string>('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [settings, setSettings] = useState<AppSettings>({
    showCapacityIndicator: true,
    allowRegistration: true
  });

  useEffect(() => {
    if (event && event.locations && event.locations[0]) {
      const location = event.locations[0];
      if (location.latitude && location.longitude) {
        // Use OpenStreetMap embed (no API key required)
        const lat = location.latitude;
        const lng = location.longitude;
        setMapUrl(`https://www.openstreetmap.org/export/embed.html?bbox=${lng-0.005},${lat-0.003},${lng+0.005},${lat+0.003}&layer=mapnik&marker=${lat},${lng}`);
      }
    }
  }, [event]);

  // Update current time every second for live updates
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (event) {
      setShareUrl(`${window.location.origin}/events/${event.id}`);
    }
  }, [event]);

  // Load settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const publicSettings = await settingsAPI.getPublicSettings();
        // Handle nested settings structure and multiple possible keys
        const showCapacity = publicSettings.events?.showCapacityIndicator 
          ?? publicSettings.showCapacityIndicator 
          ?? publicSettings.features?.show_capacity_indicator 
          ?? true;
        const registrationEnabled = publicSettings.users?.registration_enabled 
          ?? publicSettings.allowRegistration 
          ?? true;
        
        setSettings({
          showCapacityIndicator: showCapacity,
          allowRegistration: registrationEnabled
        });
      } catch (error) {
        console.error('Error loading settings:', error);
        // Keep default values on error
      }
    };
    loadSettings();
  }, []);

  if (!isOpen || !event) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateDuration = () => {
    const start = new Date(event.startTime);
    const end = new Date(event.endTime);
    const durationMs = end.getTime() - start.getTime();
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}min`;
    } else if (hours > 0) {
      return `${hours} hora${hours > 1 ? 's' : ''}`;
    } else {
      return `${minutes} minutos`;
    }
  };

  const getStatusColor = () => {
    switch (event.status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'UPCOMING':
        return 'bg-blue-100 text-blue-800';
      case 'FINISHED':
        return 'bg-gray-100 text-gray-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = () => {
    switch (event.status) {
      case 'ACTIVE':
        return 'En Curso';
      case 'UPCOMING':
        return 'Próximo';
      case 'FINISHED':
        return 'Finalizado';
      case 'CANCELLED':
        return 'Cancelado';
      default:
        return '';
    }
  };

  // Calculate progress for ACTIVE events
  const calculateProgress = () => {
    if (event.status !== 'ACTIVE') return { percentage: 0, timeRemaining: '', isActive: false };
    
    const start = new Date(event.startTime).getTime();
    const end = new Date(event.endTime).getTime();
    const current = currentTime.getTime();
    
    const totalDuration = end - start;
    const elapsed = current - start;
    const remaining = end - current;
    
    const percentage = Math.max(0, Math.min(100, (elapsed / totalDuration) * 100));
    
    // Format time remaining
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
    
    let timeRemaining = '';
    if (hours > 0) {
      timeRemaining = `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      timeRemaining = `${minutes}m ${seconds}s`;
    } else {
      timeRemaining = `${seconds}s`;
    }
    
    return { percentage, timeRemaining, isActive: true };
  };

  // Calculate countdown for UPCOMING events
  const calculateCountdown = () => {
    if (event.status !== 'UPCOMING') return { timeUntilStart: '', isUpcoming: false };
    
    const start = new Date(event.startTime).getTime();
    const current = currentTime.getTime();
    const timeUntil = start - current;
    
    if (timeUntil <= 0) return { timeUntilStart: 'Comenzando ahora', isUpcoming: true };
    
    const days = Math.floor(timeUntil / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeUntil % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeUntil % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeUntil % (1000 * 60)) / 1000);
    
    let timeUntilStart = '';
    if (days > 0) {
      timeUntilStart = `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      timeUntilStart = `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      timeUntilStart = `${minutes}m ${seconds}s`;
    } else {
      timeUntilStart = `${seconds}s`;
    }
    
    return { timeUntilStart, isUpcoming: true };
  };

  const handleShare = async (platform?: string) => {
    const encodedTitle = encodeURIComponent(event.title);
    const encodedText = encodeURIComponent(event.description);
    const encodedUrl = encodeURIComponent(shareUrl);
    
    switch (platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodedTitle}%20${encodedUrl}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank');
        break;
      case 'copy':
        try {
          await navigator.clipboard.writeText(shareUrl);
          alert('¡Enlace copiado al portapapeles!');
        } catch (error) {
          console.error('Error copying to clipboard:', error);
        }
        break;
      default:
        if (navigator.share) {
          try {
            await navigator.share({
              title: event.title,
              text: event.description,
              url: shareUrl
            });
          } catch (error) {
            console.error('Error sharing:', error);
          }
        } else {
          // Fallback: Copy to clipboard
          try {
            await navigator.clipboard.writeText(shareUrl);
            alert('¡Enlace copiado al portapapeles!');
          } catch (error) {
            console.error('Error sharing:', error);
          }
        }
    }
  };

  const openMaps = (location: Location) => {
    if (location.latitude && location.longitude) {
      const coords = `${location.latitude},${location.longitude}`;
      // Detect platform and open appropriate maps app
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        window.open(`maps://maps.google.com/maps?q=${coords}`, '_blank');
      } else {
        window.open(`https://www.google.com/maps/search/?api=1&query=${coords}`, '_blank');
      }
    } else if (location.address) {
      const encodedAddress = encodeURIComponent(`${location.address}, ${location.city || ''}`);
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
    }
  };

  const availableSeats = event.capacity ? event.capacity - event.currentAttendees : null;
  const progress = calculateProgress();
  const countdown = calculateCountdown();

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
        <motion.div
          initial={{ opacity: 0, y: '100%' }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: '100%' }}
          transition={{ type: "spring", damping: 25, stiffness: 120 }}
          className="bg-white rounded-t-3xl sm:rounded-xl shadow-2xl w-full sm:max-w-4xl h-[95vh] sm:max-h-[90vh] overflow-hidden backdrop-blur-sm border border-purple-100"
        >
          {/* Enhanced Header with Featured Image */}
          <div className="relative h-48 sm:h-64 bg-gradient-to-br from-purple-600 via-violet-600 to-purple-800">
            {event.imageUrl ? (
              <div className="relative w-full h-full">
                <img
                  src={event.imageUrl}
                  alt={event.title}
                  className="w-full h-full object-cover transition-opacity duration-300"
                  loading="lazy"
                />
                {/* Skeleton loader */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-300 to-purple-500 animate-pulse opacity-0" />
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-600 to-violet-800">
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                >
                  <Calendar className="w-24 h-24 text-white opacity-50" />
                </motion.div>
              </div>
            )}
            
            {/* Enhanced overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
            
            {/* Glassmorphism effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
            
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm rounded-full p-2 text-white hover:bg-white/30 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Enhanced Title and Status */}
            <div className="absolute bottom-4 left-4 sm:left-6 right-4 sm:right-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <motion.h2 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-xl sm:text-3xl font-bold text-white mb-2 sm:mb-3 drop-shadow-lg"
                  >
                    {event.title}
                  </motion.h2>
                  
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="flex items-center gap-2 sm:gap-3 flex-wrap"
                  >
                    <span className={`px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-semibold backdrop-blur-sm border shadow-lg ${getStatusColor()} ${event.status === 'ACTIVE' ? 'ring-2 ring-white/30' : ''}`}>
                      {getStatusText()}
                    </span>
                    
                    {event.isStreaming && (
                      <motion.span 
                        animate={{ scale: [1, 1.05, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-1 sm:py-2 rounded-full bg-red-500/90 text-white text-xs sm:text-sm font-semibold backdrop-blur-sm border border-red-300/50 shadow-lg"
                      >
                        <div className="relative flex items-center">
                          <Video className="w-3 h-3 sm:w-4 sm:h-4" />
                          <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-300 rounded-full animate-pulse" />
                        </div>
                        <span className="hidden sm:inline">EN VIVO</span>
                        <span className="sm:hidden">VIVO</span>
                      </motion.span>
                    )}

                    {event.status === 'ACTIVE' && (
                      <motion.span 
                        animate={{ opacity: [0.7, 1, 0.7] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/90 text-white text-sm font-semibold backdrop-blur-sm border border-green-300/50 shadow-lg"
                      >
                        <Zap className="w-4 h-4" />
                        ACTIVO
                      </motion.span>
                    )}
                  </motion.div>

                  {/* Progress bar for ACTIVE events */}
                  {progress.isActive && (
                    <motion.div 
                      initial={{ opacity: 0, scaleX: 0 }}
                      animate={{ opacity: 1, scaleX: 1 }}
                      transition={{ delay: 0.4 }}
                      className="relative mt-4 bg-black/20 backdrop-blur-sm rounded-full h-3 overflow-hidden border border-white/20"
                    >
                      <motion.div
                        className="h-full bg-gradient-to-r from-green-400 to-blue-500 rounded-full shadow-lg"
                        initial={{ width: '0%' }}
                        animate={{ width: `${progress.percentage}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center text-white text-xs font-semibold drop-shadow">
                        {Math.round(progress.percentage)}% • {progress.timeRemaining} restante
                      </div>
                    </motion.div>
                  )}

                  {/* Countdown for UPCOMING events */}
                  {countdown.isUpcoming && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-500/20 backdrop-blur-sm rounded-full border border-blue-300/30"
                    >
                      <Timer className="w-4 h-4 text-blue-200" />
                      <span className="text-blue-100 text-sm font-semibold">
                        Comienza en: {countdown.timeUntilStart}
                      </span>
                    </motion.div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Content */}
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(95vh - 12rem)' }}>
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-8">
              {/* Enhanced Action Buttons */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="flex flex-col sm:flex-row gap-2 sm:gap-3"
              >
                {event.status !== 'FINISHED' && event.status !== 'CANCELLED' && (
                  <>
                    {settings.allowRegistration && (
                      <button
                        onClick={() => onRegister?.(event.id)}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                          isRegistered
                            ? 'bg-green-100 text-green-800'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}
                      >
                        {isRegistered ? (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            Registrado
                          </>
                        ) : (
                          <>
                            <Users className="w-4 h-4" />
                            Registrarse
                          </>
                        )}
                      </button>
                    )}

                    <button
                      onClick={() => onSetReminder?.(event.id)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                        hasReminder
                          ? 'bg-blue-100 text-blue-800'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Bell className="w-4 h-4" />
                      {hasReminder ? 'Recordatorio Activo' : 'Recordar'}
                    </button>
                  </>
                )}
                
                {/* Enhanced Social Share Buttons */}
                <div className="flex items-center gap-2 justify-center sm:justify-start overflow-x-auto">
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex-shrink-0 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl hover:scale-105"
                    title="Compartir en WhatsApp"
                  >
                    <MessageCircle className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex-shrink-0 px-3 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 font-medium transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl hover:scale-105"
                    title="Compartir en Twitter"
                  >
                    <Twitter className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex-shrink-0 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl hover:scale-105"
                    title="Compartir en Facebook"
                  >
                    <Facebook className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => handleShare('copy')}
                    className="flex-shrink-0 px-3 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 font-medium transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl hover:scale-105"
                    title="Copiar enlace"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => handleShare()}
                    className="flex-shrink-0 px-4 py-2 border-2 border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 hover:border-purple-400 font-medium transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl hover:scale-105"
                  >
                    <Share2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Más</span>
                  </button>
                </div>

                {event.isStreaming && event.streamUrl && (
                  <a
                    href={event.streamUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors flex items-center gap-2"
                  >
                    <Video className="w-4 h-4" />
                    Ver Transmisión
                  </a>
                )}
              </motion.div>

              {/* Enhanced Date, Time and Duration */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-4 sm:p-6 border border-slate-200/50 shadow-sm"
              >
                <div className="flex items-center sm:items-start gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg sm:rounded-xl">
                    <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-purple-700" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wide">Fecha</p>
                    <p className="font-semibold text-gray-900 text-base sm:text-lg">{formatDate(event.startTime)}</p>
                  </div>
                </div>
                <div className="flex items-center sm:items-start gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg sm:rounded-xl">
                    <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-blue-700" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wide">Horario</p>
                    <p className="font-semibold text-gray-900 text-base sm:text-lg">
                      {formatTime(event.startTime)} - {formatTime(event.endTime)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center sm:items-start gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-lg sm:rounded-xl">
                    <Timer className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-700" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wide">Duración</p>
                    <p className="font-semibold text-gray-900 text-base sm:text-lg">{calculateDuration()}</p>
                  </div>
                </div>
              </motion.div>

              {/* Enhanced Description */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-xl p-6 border border-slate-200/50 shadow-sm"
              >
                <h3 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-lg">
                    <Globe className="w-5 h-5 text-indigo-700" />
                  </div>
                  Acerca del Evento
                </h3>
                <p className="text-gray-700 leading-relaxed text-base">{event.description}</p>
              </motion.div>

              {/* Enhanced Categories */}
              {event.categories && event.categories.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white rounded-xl p-6 border border-slate-200/50 shadow-sm"
                >
                  <h3 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg">
                      <Tag className="w-5 h-5 text-purple-700" />
                    </div>
                    Categorías
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {event.categories.map((category, index) => (
                      <motion.span
                        key={category.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 * index }}
                        className="px-4 py-2 rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105 border"
                        style={{
                          backgroundColor: category.color ? `${category.color}20` : '#f3f4f6',
                          color: category.color || '#374151',
                          borderColor: category.color ? `${category.color}40` : '#d1d5db'
                        }}
                      >
                        <Badge className="w-3 h-3 mr-1 inline" />
                        {category.name}
                      </motion.span>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Significantly Enhanced Locations Section */}
              {((event.locations && event.locations.length > 0) || event.location) && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-white rounded-xl p-6 border border-slate-200/50 shadow-sm"
                >
                  <h3 className="text-xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-red-100 to-red-200 rounded-lg">
                      <MapPin className="w-5 h-5 text-red-700" />
                    </div>
                    Ubicación{event.EventLocations && event.EventLocations.length > 1 ? 'es' : ''}
                  </h3>
                  
                  {/* Show detailed locations if available - check EventLocations first, then locations array */}
                  {event.EventLocations && event.EventLocations.length > 0 ? (
                    event.EventLocations.map((eventLocation, index) => (
                      <motion.div 
                        key={eventLocation.Location.id} 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 * index }}
                        className="mb-6 last:mb-0"
                      >
                        <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-slate-200/50">
                          {/* Location Image - Now prominent */}
                          {eventLocation.Location.imageUrl && (
                            <div className="mb-4 rounded-xl overflow-hidden shadow-md">
                              <img
                                src={eventLocation.Location.imageUrl.startsWith('http') 
                                  ? eventLocation.Location.imageUrl 
                                  : `${import.meta.env.VITE_API_URL || ''}${eventLocation.Location.imageUrl}`}
                                alt={eventLocation.Location.name}
                                className="w-full h-48 object-cover"
                                loading="lazy"
                              />
                            </div>
                          )}
                          
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h4 className="font-bold text-gray-900 text-lg mb-2">
                                {eventLocation.Location.name}
                              </h4>
                              <p className="text-gray-600 mb-2 flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-gray-400" />
                                {eventLocation.Location.address}
                              </p>
                              {eventLocation.Location.city && (
                                <p className="text-sm text-gray-500 mb-3">{eventLocation.Location.city}</p>
                              )}
                            </div>
                          </div>
                          
                          {/* Location Details */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            {eventLocation.Location.capacity && (
                              <div className="flex items-center gap-3 text-sm">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                  <Users className="w-4 h-4 text-blue-600" />
                                </div>
                                <div>
                                  <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">Capacidad</p>
                                  <p className="font-semibold text-gray-900">{eventLocation.Location.capacity} personas</p>
                                </div>
                              </div>
                            )}
                            
                            {(eventLocation.Location.latitude && eventLocation.Location.longitude) && (
                              <div className="flex items-center gap-3 text-sm">
                                <div className="p-2 bg-green-100 rounded-lg">
                                  <MapPin className="w-4 h-4 text-green-600" />
                                </div>
                                <div>
                                  <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">Coordenadas</p>
                                  <p className="font-semibold text-gray-900">{eventLocation.Location.latitude.toFixed(4)}, {eventLocation.Location.longitude.toFixed(4)}</p>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Amenities */}
                          {location.amenities && location.amenities.length > 0 && (
                            <div className="mb-4">
                              <p className="text-sm font-medium text-gray-700 mb-2">Servicios disponibles:</p>
                              <div className="flex flex-wrap gap-2">
                                {location.amenities.map((amenity, i) => (
                                  <span
                                    key={i}
                                    className="px-3 py-1 bg-white rounded-lg text-xs font-medium text-gray-600 border border-gray-200 shadow-sm"
                                  >
                                    {amenity}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Enhanced Maps Button */}
                          <motion.button
                            onClick={() => openMaps(location)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-3 group"
                          >
                            <MapPin className="w-5 h-5 group-hover:scale-110 transition-transform" />
                            <span>Abrir en Mapas</span>
                            <ExternalLink className="w-4 h-4 group-hover:scale-110 transition-transform" />
                          </motion.button>
                        </div>
                        
                        {/* Enhanced Map Embed */}
                        {eventLocation.Location.latitude && eventLocation.Location.longitude && (
                          <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="mt-4 rounded-xl overflow-hidden h-64 shadow-lg border border-slate-200"
                          >
                            <iframe
                              title={`Mapa de ${eventLocation.Location.name}`}
                              width="100%"
                              height="100%"
                              frameBorder="0"
                              src={`https://maps.google.com/maps?q=${eventLocation.Location.latitude},${eventLocation.Location.longitude}&z=15&output=embed`}
                              allowFullScreen
                              loading="lazy"
                              className="w-full h-full"
                            />
                          </motion.div>
                        )}
                      </motion.div>
                    ))
                  ) : (
                    /* Show simple location string if no detailed locations */
                    event.location && (
                      <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-slate-200/50">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-gradient-to-br from-red-100 to-red-200 rounded-xl">
                            <MapPin className="w-6 h-6 text-red-700" />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900 text-lg">{event.location}</p>
                            <p className="text-gray-500 text-sm mt-1">Ubicación del evento</p>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </motion.div>
              )}

              {/* Enhanced Speakers Section */}
              {event.speakers && event.speakers.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="bg-white rounded-xl p-6 border border-slate-200/50 shadow-sm"
                >
                  <h3 className="text-xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-amber-100 to-amber-200 rounded-lg">
                      <User className="w-5 h-5 text-amber-700" />
                    </div>
                    Ponentes
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {event.speakers.map((speaker, index) => (
                      <motion.div 
                        key={speaker.id} 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 * index }}
                        className="flex items-start gap-4 bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-4 border border-slate-200/50 hover:shadow-lg transition-all duration-200 hover:scale-[1.02]"
                      >
                        <div className="relative">
                          {speaker.imageUrl ? (
                            <div className="relative">
                              <img
                                src={speaker.imageUrl}
                                alt={speaker.name}
                                className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                                loading="lazy"
                              />
                              {/* Skeleton loader */}
                              <div className="absolute inset-0 bg-gray-300 rounded-full animate-pulse opacity-0" />
                            </div>
                          ) : (
                            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-200 to-purple-300 flex items-center justify-center border-4 border-white shadow-lg">
                              <User className="w-10 h-10 text-purple-700" />
                            </div>
                          )}
                          {/* Status indicator */}
                          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                            <CheckCircle className="w-3 h-3 text-white" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-gray-900 text-lg mb-1">{speaker.name}</h4>
                          {speaker.title && (
                            <p className="text-sm font-medium text-purple-600 mb-2">{speaker.title}</p>
                          )}
                          {speaker.bio && (
                            <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">{speaker.bio}</p>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Enhanced Capacity Info */}
              {settings.showCapacityIndicator && event.capacity && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200/50 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl">
                        <Users className="w-6 h-6 text-blue-700" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Aforo del Evento</p>
                        <p className="font-bold text-gray-900 text-xl">
                          {event.currentAttendees.toLocaleString()} / {event.capacity.toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-600">asistentes registrados</p>
                      </div>
                    </div>
                    {availableSeats !== null && (
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-1">Plazas disponibles</p>
                        <p className={`font-bold text-2xl ${
                          availableSeats <= 10 ? 'text-red-600' : availableSeats <= 50 ? 'text-yellow-600' : 'text-green-600'
                        }`}>
                          {availableSeats.toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Enhanced Progress bar */}
                  <div className="relative">
                    <div className="bg-white rounded-full h-4 shadow-inner border border-gray-200">
                      <motion.div
                        className={`h-full rounded-full transition-all duration-1000 ${
                          (event.currentAttendees / event.capacity) > 0.9
                            ? 'bg-gradient-to-r from-red-500 to-red-600'
                            : (event.currentAttendees / event.capacity) > 0.7
                            ? 'bg-gradient-to-r from-yellow-500 to-orange-500'
                            : 'bg-gradient-to-r from-green-500 to-emerald-500'
                        } shadow-sm`}
                        initial={{ width: '0%' }}
                        animate={{ width: `${(event.currentAttendees / event.capacity) * 100}%` }}
                        transition={{ duration: 1.5, ease: "easeOut" }}
                      />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xs font-semibold text-gray-700">
                        {Math.round((event.currentAttendees / event.capacity) * 100)}% ocupado
                      </span>
                    </div>
                  </div>
                  
                  {/* Capacity status indicator */}
                  <div className="mt-4 flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${
                      availableSeats && availableSeats <= 10 ? 'bg-red-500 animate-pulse' : 
                      availableSeats && availableSeats <= 50 ? 'bg-yellow-500' : 'bg-green-500'
                    }`} />
                    <span className="text-sm font-medium text-gray-600">
                      {availableSeats && availableSeats <= 10 ? 'Últimas plazas disponibles' :
                       availableSeats && availableSeats <= 50 ? 'Plazas limitadas' : 'Buena disponibilidad'}
                    </span>
                  </div>
                </motion.div>
              )}

              {/* Enhanced Tags Section */}
              {event.tags && event.tags.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="bg-white rounded-xl p-6 border border-slate-200/50 shadow-sm"
                >
                  <h3 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-cyan-100 to-cyan-200 rounded-lg">
                      <Tag className="w-5 h-5 text-cyan-700" />
                    </div>
                    Etiquetas
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {event.tags.map((tag, index) => {
                      const colors = [
                        'from-pink-100 to-pink-200 text-pink-700 border-pink-200',
                        'from-blue-100 to-blue-200 text-blue-700 border-blue-200',
                        'from-green-100 to-green-200 text-green-700 border-green-200',
                        'from-yellow-100 to-yellow-200 text-yellow-700 border-yellow-200',
                        'from-purple-100 to-purple-200 text-purple-700 border-purple-200',
                        'from-indigo-100 to-indigo-200 text-indigo-700 border-indigo-200',
                      ];
                      const colorClass = colors[index % colors.length];
                      
                      return (
                        <motion.span
                          key={index}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.05 * index }}
                          className={`px-4 py-2 bg-gradient-to-r ${colorClass} rounded-xl text-sm font-semibold border shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105 cursor-default`}
                        >
                          #{tag}
                        </motion.span>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};